import {Badge} from 'reactstrap';
import {components as selectComponents} from 'react-select';
import React, {useEffect, useState} from 'react';

import PropTypes from 'prop-types';
import {includes, isEmpty, isEqual, map, size, union} from 'lodash';

import {alertByError} from 'utils/alerts';
import {getFilterTags} from 'api/watchlist';
import InputCreatable from './InputCreatable';
import cx from 'classnames';

InputTags.propTypes = {
  name: PropTypes.string,
  placeholder: PropTypes.string,
  menuPlaceholder: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.object),
  action: PropTypes.func,
};

InputTags.defaultProps = {
  options: [],
  name: 'tags',
  placeholder: 'Choose a tag or create a new one',
  menuPlaceholder: 'Add tags',
  action: getFilterTags,
  isMulti: true,
  isClearable: true,
  cacheOptions: true,
  hideSelectedOptions: false,
  backspaceRemovesValue: true,
  createOptionPosition: 'first',
  getOptionLabel: (option) => option.label,
  getOptionValue: (option) => option.label,
};

const createOption = (value) => ({label: value.name, value: value.id});

const NoOptionsMessage = (props) => {
  const {options, test, placeholder, ...rest} = props;
  return (
    <selectComponents.NoOptionsMessage {...rest}>
      {isEmpty(options) && placeholder}
    </selectComponents.NoOptionsMessage>
  );
};

const Option = (props) => {
  const {children, isSelected, value, ...rest} = props;

  return (
    <selectComponents.Option {...rest}>
      <div className="d-flex flex-row justify-content-between">
        {isSelected ? (
          <Badge color="secondary">{value}</Badge>
        ) : (
          <span className="overflow-hidden text-primary">{value}</span>
        )}
      </div>
    </selectComponents.Option>
  );
};

const INPUT_LIMIT = 20;

function InputTags(props) {
  const {
    name,
    placeholder,
    options,
    components,
    menuPlaceholder,
    icon,
    action,
    ...rest
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [defaultOptions, setDefaultOptions] = useState([]);

  async function loadOptions(value, callback) {
    try {
      const {data} = await action({
        search: inputValue || value,
      });
      return callback(map(data, (v) => createOption(v)));
    } catch (error) {
      alertByError(error);
    }
  }

  async function getDefaultOptions() {
    setIsLoading(true);
    try {
      const {data} = await getFilterTags();
      setDefaultOptions(map(data, (v) => createOption(v)));
    } catch (error) {
      alertByError(error);
    } finally {
      setIsLoading(false);
    }
  }

  const [value, setValue] = useState([] || options);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (options) {
      setValue([...options.map((v) => createOption(v))]);
    }
  }, [options]);

  function handleInputChange(value) {
    const _value = isEqual(size(value), 50) ? inputValue : value;
    setInputValue(_value);
  }

  function handleOnChange(keywordValue, meta) {
    const {action} = meta;
    const values = value.map((v) => v.value);

    switch (action) {
      case 'create-option':
        if (value.length < 20) {
          return setValue((prev) => union(prev, keywordValue));
        }
        break;

      case 'pop-value':
      case 'remove-value':
      case 'select-option':
        setValue(keywordValue);
        break;

      case 'deselect-option':
        if (includes(values, inputValue)) setInputValue('');
        break;

      case 'clear':
        setValue([]);
        break;

      default:
        break;
    }
  }

  async function onMenuOpen() {
    await getDefaultOptions();
  }

  const _components = {
    DropdownIndicator: null,
    IndicatorSeparator: null,
    Option,
    NoOptionsMessage: (props) => (
      <NoOptionsMessage {...props} placeholder={menuPlaceholder} />
    ),
    ...components,
  };

  const isLimit = value.length === INPUT_LIMIT;
  const feedback = isLimit
    ? 'You have reached the maximum number of tags.'
    : `${value.length}/${INPUT_LIMIT} tags`;

  return (
    <div className="d-flex flex-column w-100">
      <div className="d-flex flex-row align-items-center flex-grow-1">
        <div className="flex-grow-1">
          <InputCreatable
            name={name}
            value={value}
            options={value}
            isLoading={isLoading}
            inputValue={inputValue}
            onMenuOpen={onMenuOpen}
            components={_components}
            onChange={handleOnChange}
            placeholder={placeholder}
            loadOptions={loadOptions}
            onInputChange={handleInputChange}
            defaultOptions={defaultOptions}
            {...rest}
          />
        </div>
        {icon}
      </div>
      <small
        className={cx('align-self-end mt-1', {
          'text-muted': !isLimit,
          'text-danger': isLimit,
          'mr-4': !!icon,
        })}
      >
        {feedback}
      </small>
    </div>
  );
}

export default InputTags;
